/**
 * Связь с сервером.
 *
 * Ключ доступа живёт 15 минут, поэтому истечение — обычное дело, а не сбой.
 * При ответе «нужно войти» запрос молча повторяется с обновлённым ключом:
 * человек не должен замечать, что где-то что-то истекло.
 *
 * Обновление идёт в один поток: если пять запросов истекли одновременно,
 * ключ обновляется один раз, а не пять — иначе сервер погасит его как
 * повторно использованный и выкинет из приложения.
 */

const ACCESS_KEY = 'family.access'
const REFRESH_KEY = 'family.refresh'

export class ApiError extends Error {
  status: number
  code: string
  fields?: { field: string; message: string }[]

  constructor(status: number, code: string, message: string, fields?: { field: string; message: string }[]) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.fields = fields
  }
}

// Приватный режим в Safari умеет бросать на запись в хранилище
const safeStorage = {
  get(key: string): string | null {
    try {
      return localStorage.getItem(key)
    } catch {
      return null
    }
  },
  set(key: string, value: string): void {
    try {
      localStorage.setItem(key, value)
    } catch {
      /* останемся в памяти на время сеанса */
    }
  },
  remove(key: string): void {
    try {
      localStorage.removeItem(key)
    } catch {
      /* ничего не поделать */
    }
  },
}

let accessToken: string | null = safeStorage.get(ACCESS_KEY)
let refreshToken: string | null = safeStorage.get(REFRESH_KEY)
let refreshing: Promise<string> | null = null

const listeners = new Set<() => void>()

export const onAuthLost = (fn: () => void): (() => void) => {
  listeners.add(fn)
  return () => listeners.delete(fn)
}

export function getAccessToken(): string | null {
  return accessToken
}

export function setTokens(access: string, refresh: string): void {
  accessToken = access
  refreshToken = refresh
  safeStorage.set(ACCESS_KEY, access)
  safeStorage.set(REFRESH_KEY, refresh)
}

export function clearTokens(): void {
  accessToken = null
  refreshToken = null
  safeStorage.remove(ACCESS_KEY)
  safeStorage.remove(REFRESH_KEY)
}

export const hasSession = (): boolean => refreshToken !== null

/**
 * Обновить ключ доступа.
 *
 * Наружу вынесено ради постоянного соединения: там нет обычного
 * ответа «401», сервер просто закрывает сокет — и без этого клиент
 * ломился бы раз в секунду с протухшим ключом до скончания века.
 */
export async function renewAccess(): Promise<boolean> {
  try {
    await refreshAccess()
    return true
  } catch {
    return false
  }
}

async function refreshAccess(): Promise<string> {
  if (!refreshToken) throw new ApiError(401, 'NO_SESSION', 'Нужно войти заново')

  // Уже обновляем — подключаемся к той же попытке
  if (refreshing) return refreshing

  refreshing = (async () => {
    let response: Response
    try {
      response = await fetch('/api/auth/refresh', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      })
    } catch {
      throw new ApiError(0, 'OFFLINE', 'Нет связи с сервером. Проверь интернет')
    }

    /**
     * Выходим из аккаунта, только если сервер ОТКАЗАЛ.
     *
     * Раньше выход случался при любом неуспехе — в том числе когда
     * сервер перезапускался при выкладке и отвечал 502. Человек открывал
     * приложение в эту секунду и видел форму входа, хотя его вход был
     * жив и здоров. Отсюда «каждый раз спрашивает пароль».
     */
    if (response.status === 400 || response.status === 401 || response.status === 403) {
      clearTokens()
      listeners.forEach((fn) => fn())
      throw new ApiError(401, 'SESSION_EXPIRED', 'Нужно войти заново')
    }

    if (!response.ok) {
      throw new ApiError(response.status, 'RESTARTING', 'Сервер обновляется. Попробуй ещё раз через несколько секунд')
    }

    const data = (await response.json()) as { accessToken: string; refreshToken: string }
    setTokens(data.accessToken, data.refreshToken)
    return data.accessToken
  })()

  try {
    return await refreshing
  } finally {
    refreshing = null
  }
}

type Options = {
  method?: string
  body?: unknown
  /** Служебный признак: повтор после обновления ключа делается один раз */
  retried?: boolean
  /** Сколько раз уже повторяли, пока сервер перезапускался */
  waited?: number
}

/**
 * Сервер перезапускается — обычно при выкладке обновления. На эти
 * секунды nginx отвечает 502 или 503: запрос до приложения даже
 * не дошёл, и повторить его безопасно — ничего не выполнилось дважды.
 */
const RESTARTING = new Set([502, 503])
const pause = (ms: number) => new Promise((done) => setTimeout(done, ms))

export async function api<T>(path: string, options: Options = {}): Promise<T> {
  const { method = 'GET', body, retried = false, waited = 0 } = options

  let response: Response
  try {
    response = await fetch(path, {
      method,
      headers: {
        ...(body ? { 'Content-Type': 'application/json' } : {}),
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      },
      ...(body ? { body: JSON.stringify(body) } : {}),
    })
  } catch {
    throw new ApiError(0, 'OFFLINE', 'Нет связи с сервером. Проверь интернет')
  }

  /**
   * Сервер перезапускается — подождём и повторим сами.
   *
   * Раньше человек в эти секунды получал «Что-то пошло не так» —
   * и думал, что ошибся паролем или что приложение сломалось. А оно
   * просто обновлялось. Три попытки с паузой — это восемь секунд:
   * столько перезапуск и длится.
   */
  if (RESTARTING.has(response.status)) {
    if (waited < 3) {
      await pause(1500 + waited * 1000)
      return api<T>(path, { ...options, waited: waited + 1 })
    }
    throw new ApiError(
      response.status,
      'RESTARTING',
      'Сервер обновляется. Попробуй ещё раз через несколько секунд',
    )
  }

  if (response.status === 401 && !retried && refreshToken) {
    await refreshAccess()
    return api<T>(path, { ...options, retried: true })
  }

  if (response.status === 204) return undefined as T

  const data = await response.json().catch(() => ({}))

  if (!response.ok) {
    const error = data as { error?: string; message?: string; fields?: { field: string; message: string }[] }
    if (response.status === 401) {
      clearTokens()
      listeners.forEach((fn) => fn())
    }
    throw new ApiError(
      response.status,
      error.error ?? 'UNKNOWN',
      error.message ?? 'Что-то пошло не так',
      error.fields,
    )
  }

  return data as T
}

/**
 * Отправка файлов с показом хода загрузки.
 *
 * Через fetch ход отправки не узнать — он сообщает только о получении
 * ответа. А фотография с телефона на мобильной сети грузится секунды,
 * и всё это время человек должен видеть, что что-то происходит,
 * а не гадать, нажалась ли кнопка.
 */
export function upload<T>(
  path: string,
  form: FormData,
  onProgress?: (percent: number) => void,
): Promise<T> {
  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest()
    request.open('POST', path)

    if (accessToken) request.setRequestHeader('Authorization', `Bearer ${accessToken}`)

    request.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable && onProgress) {
        onProgress(Math.round((event.loaded / event.total) * 100))
      }
    })

    request.addEventListener('load', () => {
      let data: unknown = {}
      try {
        data = JSON.parse(request.responseText)
      } catch {
        /* пустой или испорченный ответ */
      }

      if (request.status >= 200 && request.status < 300) {
        resolve(data as T)
        return
      }

      const error = data as { error?: string; message?: string }
      reject(
        new ApiError(
          request.status,
          error.error ?? 'UPLOAD_FAILED',
          error.message ?? 'Не удалось отправить файл',
        ),
      )
    })

    request.addEventListener('error', () =>
      reject(new ApiError(0, 'NETWORK', 'Нет связи с сервером')),
    )
    request.addEventListener('abort', () =>
      reject(new ApiError(0, 'ABORTED', 'Отправка отменена')),
    )

    request.send(form)
  })
}
